import CreateNewOrgModal from './CreateNewOrgModal';
import PropTypes from 'prop-types';
import React, {Fragment, useCallback, useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {highlightMatch} from '@account/utils/highlight-match';
import {trackEvent} from '@account/analytics';
import {useDebounce} from 'use-debounce';
import {useOrgAutosuggest} from '@hooks/data/organizations/useOrgAutosuggest';

const AutocompleteOption = ({orgId, name, parentOrganizationName, ...rest}) => (
  <Stack
    {...rest}
    spacing={0.5}
    sx={theme => ({
      alignItems: 'start',
      ':not(:first-of-type)': {
        borderTop: 'solid 1px',
        borderTopColor: theme.palette.grey[300],
      },
      textWrap: 'wrap',
      wordBreak: 'break-word',
    })}
  >
    <Typography alignSelf="start">{name}</Typography>
    <Typography
      variant="caption"
      color="text.secondary"
      alignSelf="start"
      letterSpacing={0}
    >
      {orgId}
      {parentOrganizationName && (
        <> &middot; Sub-org of {parentOrganizationName}</>
      )}
    </Typography>
  </Stack>
);

AutocompleteOption.propTypes = {
  name: PropTypes.array,
  orgId: PropTypes.number,
  parentOrganizationName: PropTypes.string,
  props: PropTypes.object,
};

const CreateNewOrgLink = ({onClick, sx}) => (
  <Typography
    color="primary.main"
    onClick={onClick}
    sx={theme => ({
      cursor: 'pointer',
      ...(typeof sx === 'function' ? sx(theme) : sx),
    })}
  >
    Create a new organization &rarr;
  </Typography>
);

CreateNewOrgLink.propTypes = {
  onClick: PropTypes.func,
  sx: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

const OrgAutocomplete = ({
  contractId,
  excludeSuborgs,
  id = 'selected-org',
  limit = 8,
  onChange,
  placeholder = 'Search for an organization',
  setOrgCreateSuccessMessage,
  showCreateOrgLink = true,
  sx,
  value,
  ...rest
}) => {
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(orgSearchTerm.toLowerCase(), 300);
  const [createNewOrgModalOpen, setCreateNewOrgModalOpen] = useState(false);

  const {
    isFetching: isFetchingOrg,
    isLoading: isLoadingOrg,
    data,
  } = useOrgAutosuggest(
    {
      limit,
      search: debouncedSearchTerm ? debouncedSearchTerm : '',
      excludeSuborgs,
    },
    {
      enabled: !!debouncedSearchTerm,
      staleTime: 1000 * 60,
      keepPreviousData: false,
    }
  );

  // Gate the options on the live search term rather than evicting the cache on
  // close. The old removeQueries() ran while the debounced term was still
  // populated, so it re-enabled the query and refetched the entry it had just
  // dropped - a wasted request per close - and its prefix match also cleared
  // sibling pickers mounted on the same page. Reading [] until the user types
  // again gets the same "no stale options on reopen" behaviour for free.
  const orgAutocompleteOptions = orgSearchTerm ? (data?.results ?? []) : [];

  const handleClose = useCallback(() => setOrgSearchTerm(''), []);

  const handleOpenCreateOrgModal = useCallback(() => {
    setCreateNewOrgModalOpen(true);
    trackEvent('Provisioning: Create New Organization clicked', {contractId});
  }, [contractId]);

  const handleCloseCreateOrgModal = useCallback(
    () => setCreateNewOrgModalOpen(false),
    []
  );

  return (
    <>
      <Autocomplete
        disablePortal
        filterOptions={x => x} // needed because we're filtering on the server
        getOptionLabel={option => option.name}
        id={id}
        ListboxProps={{sx: {paddingBottom: 0, paddingTop: 0}}}
        loading={!!orgSearchTerm && (isFetchingOrg || isLoadingOrg)}
        name="selectedOrg"
        noOptionsText={
          orgSearchTerm ? (
            <>
              No organizations found
              {showCreateOrgLink && (
                <CreateNewOrgLink onClick={handleOpenCreateOrgModal} />
              )}
            </>
          ) : (
            'Start typing to search for an organization...'
          )
        }
        onChange={(_, organization) => onChange(organization)}
        onClose={handleClose}
        options={orgAutocompleteOptions}
        renderInput={params => (
          <TextField
            {...params}
            data-testid="org-autocomplete-textbox"
            InputProps={{
              ...(params.InputProps ?? {}),
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={event => setOrgSearchTerm(event.target.value)}
            placeholder={placeholder}
            value={orgSearchTerm}
          />
        )}
        renderOption={({key, ...props}, option, state, ownerState) => {
          const onLastItem = state.index === ownerState.options.length - 1;

          return (
            <Fragment key={option.id}>
              <AutocompleteOption
                name={highlightMatch(debouncedSearchTerm, option.name)}
                orgId={option.id}
                parentOrganizationName={option.parent_organization?.name}
                {...props}
              />
              {onLastItem && showCreateOrgLink && (
                <CreateNewOrgLink
                  onClick={handleOpenCreateOrgModal}
                  sx={theme => ({
                    ':hover': {
                      background: theme.palette.grey[100],
                    },
                    borderTop: 'solid 1px',
                    borderTopColor: theme.palette.grey[300],
                    padding: 1,
                    paddingLeft: 2,
                  })}
                />
              )}
            </Fragment>
          );
        }}
        slotProps={{paper: {elevation: 4}}}
        sx={sx}
        {...rest}
        value={value}
      />

      {createNewOrgModalOpen && (
        <CreateNewOrgModal
          contractId={contractId}
          handlePostOrgCreation={onChange}
          handleModalClose={handleCloseCreateOrgModal}
          setSuccessMessage={setOrgCreateSuccessMessage}
        />
      )}
    </>
  );
};

OrgAutocomplete.propTypes = {
  contractId: PropTypes.number,
  excludeSuborgs: PropTypes.bool,
  id: PropTypes.string,
  limit: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  setOrgCreateSuccessMessage: PropTypes.func,
  showCreateOrgLink: PropTypes.bool,
  sx: PropTypes.object,
  value: PropTypes.object,
};

export default OrgAutocomplete;
